/**
 * Time Horizon Generator for Experiments
 *
 * The fixed-horizon menu (duration map, horizon tiers, and the
 * no-horizon-shorter-than-the-bar filter) lives in
 * `@einhaus/project314-types` `src/types/ExperimentTypes.ts`, shared with the
 * backend; the Rust mirror is project314-backend
 * `rust/shared/types/src/time_horizons.rs`. The shared menu returns null for
 * unknown durations — this client wrapper deliberately falls back to the
 * ultra-short-term set (the backend throws) because render paths must not
 * crash on legacy stored durations.
 */

import {
    HORIZON_END_OF_DAY,
    HORIZON_NEXT_DAY_OPEN,
    isEventHorizon,
    getEventHorizonLabel,
    getEventHorizonTooltip,
    sortHorizons,
    ULTRA_SHORT_TERM_HORIZONS,
    getTimeHorizonsForDuration as getTimeHorizonsForDurationOrNull,
    getSupportedTimeHorizonsForDuration as getSupportedTimeHorizonsForDurationOrNull
} from '@einhaus/project314-types';

// Re-export event horizon constants and helpers for convenience
export { HORIZON_END_OF_DAY, HORIZON_NEXT_DAY_OPEN, isEventHorizon, getEventHorizonLabel, getEventHorizonTooltip, sortHorizons };

type BarDuration = string;

/**
 * Generate appropriate time horizons for a given trading duration
 */
export function getTimeHorizonsForDuration(duration: BarDuration): number[] {
    const horizons = getTimeHorizonsForDurationOrNull(duration);

    if (!horizons) {
        // Client-side deviation from the backend (which throws): stored configs
        // can reference durations this build no longer knows, and a render path
        // must not crash on them.
        return [...ULTRA_SHORT_TERM_HORIZONS];
    }

    return horizons;
}

export function getSupportedTimeHorizonsForDuration(duration: BarDuration): number[] {
    return getSupportedTimeHorizonsForDurationOrNull(duration) ?? [...ULTRA_SHORT_TERM_HORIZONS];
}

/**
 * Format seconds into human-readable time string.
 * Handles both offset-based and event-based horizons.
 *
 * @param seconds - Time in seconds (or event horizon sentinel value)
 * @returns Formatted string like "5m", "1h", "2d", "EOD", "Next Open", etc.
 */
export function formatTimeHorizon(seconds: number): string {
    // Handle event-based horizons first
    const eventLabel = getEventHorizonLabel(seconds);
    if (eventLabel) return eventLabel;

    // Offset-based formatting
    const minute = 60;
    const hour = 3600;
    const day = 86400;
    const week = 604800;
    const month = 2592000;
    const year = 31536000;

    if (seconds < hour) {
        const minutes = Math.floor(seconds / minute);
        return `${minutes}m`;
    } else if (seconds < day) {
        const hours = Math.floor(seconds / hour);
        return `${hours}h`;
    } else if (seconds < week) {
        const days = Math.floor(seconds / day);
        return `${days}d`;
    } else if (seconds < month) {
        const weeks = Math.floor(seconds / week);
        return `${weeks}w`;
    } else if (seconds < year) {
        const months = Math.floor(seconds / month);
        return `${months}mo`;
    } else {
        const years = Math.floor(seconds / year);
        return `${years}y`;
    }
}
